// Copyright ©️ 2026 eVolve MEP, LLC

import { Combobox, type Primitive, isPrimitive } from '@mantine/core';

import { EvolveIcon } from 'assets/icons/EvolveIcon';

import type { TypeSafeComboboxItem, TypeSafeRenderOption } from './types';

export const normalizeSelectOption = <T extends Primitive, D extends object>(
  option: T | TypeSafeComboboxItem<T, D>,
): TypeSafeComboboxItem<T, D> =>
  isPrimitive(option)
    ? ({
        value: option,
        label: String(option),
        disabled: false,
      } as TypeSafeComboboxItem<T, D>)
    : option;

export const SelectOption = <T extends Primitive, D extends object>({
  option,
  renderOption,
  checked,
}: {
  option: T | TypeSafeComboboxItem<T, D>;
  renderOption: TypeSafeRenderOption<T, D> | undefined;
  checked: boolean;
}) => {
  const normalizedOption = normalizeSelectOption(option);
  const { value, label, disabled, color } = normalizedOption;
  return (
    <Combobox.Option active={checked} value={value} disabled={disabled} c={color}>
      {renderOption?.({ option: normalizedOption, checked }) ?? (
        <>
          {checked && (
            <EvolveIcon
              icon="Check"
              inline
              color="gray.6"
              size="sm"
              spanProps={{
                style: {
                  marginRight: 5,
                  marginTop: 2,
                },
              }}
            />
          )}
          {label}
        </>
      )}
    </Combobox.Option>
  );
};
